import {
	ClientEntityType,
	JobEntityType,
	OrganizationEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { PdfTemplateType } from "../document-template.types";
import { QRCode } from "@progress/kendo-react-barcodes";
import "../fonts.css";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { formatFullDateAndTimeLine } from "./auftragsblatt.template";

export interface JobOrderLabelTemplateProps {
	client: ClientEntityType | null;
	job: JobEntityType | null;
	organization: OrganizationEntityType | null;
	pdfTemplateConfiguration: PdfTemplateConfiguration | undefined;
	patient?: PatientEntityType;
	qrCodeUrl: string;
}

export const JobOrderLabelTemplate: React.FC<JobOrderLabelTemplateProps> = ({
	client,
	job,
	organization,
	pdfTemplateConfiguration,
	patient,
	qrCodeUrl,
}) => {
	if (!organization || !job || !client) {
		return null;
	}

	const showToothColor =
		!!pdfTemplateConfiguration?.[PdfTemplateType.ETIK_AUFTRAG]
			?.showToothColor && !!job.tooth_color;

	const hasTitle = !!job.title;
	const hasPatient = !!patient;
	const lineCount =
		1 +
		(hasTitle ? 1 : 0) +
		1 +
		(hasPatient ? 1 : 0) +
		(showToothColor ? 1 : 0);

	// Same font size for all lines, scale based on total lines
	// 5 visual rows available (~29mm / ~5.8mm per row at 9pt)
	const fontSize = lineCount <= 3 ? "10pt" : "9pt";

	let patientText: string | null = null;
	if (hasPatient) {
		let dob = "";
		const bd = patient!.birth_date;
		if (bd) {
			const parts = String(bd).split("-");
			if (parts.length === 3)
				dob = ` (*${parts[2]}.${parts[1]}.${parts[0]})`;
		}
		patientText = `${patient!.code ? patient!.code + ": " : ""}${
			patient?.first_name ?? ""
		} ${patient?.last_name ?? ""}${dob}`;
	}

	const lineStyle: React.CSSProperties = {
		fontSize,
		lineHeight: "1.3",
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "block",
	};

	return (
		<div
			style={{
				fontFamily: "LiberationSans",
				width: "100mm",
				height: "32mm",
				position: "relative",
			}}
		>
			<div
				style={{
					position: "absolute",
					top: "1mm",
					left: "69mm",
					width: "30mm",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<div style={{ width: "26mm", height: "26mm", flexShrink: 0 }}>
					<QRCode
						value={qrCodeUrl}
						errorCorrection="M"
						encoding="UTF_8"
						padding={0}
						style={{ width: "26mm", height: "26mm" }}
					/>
				</div>
				{job.outgoing_order_date && (
					<div
						style={{
							marginTop: "0.5mm",
							width: "100%",
							textAlign: "center",
							fontSize: "7pt",
							fontWeight: 600,
							lineHeight: 1.15,
							color: "#000000",
						}}
					>
						{formatFullDateAndTimeLine(
							job.outgoing_order_date,
							job.outgoing_order_time
						)}
					</div>
				)}
			</div>

			{/* Text */}
			<div
				style={{
					position: "absolute",
					top: "1.5mm",
					left: "2mm",
					width: "64mm",
					height: "29mm",
					overflow: "hidden",
				}}
			>
				{/* Code — bold, largest */}
				<div
					style={{
						...lineStyle,
						fontWeight: "bold",
						fontSize: "14pt",
					}}
				>
					{job.code}
				</div>

				{/* Title — wraps naturally, overflow hidden on parent clips it */}
				{hasTitle && (
					<div
						style={{
							...lineStyle,
							whiteSpace: "normal",
							wordBreak: "break-word",
							fontWeight: "bold",
						}}
					>
						{job.title}
					</div>
				)}

				<div
					style={{
						borderTop: "0.5pt solid #d1d5db",
						marginTop: "0.5mm",
						marginBottom: "0.5mm",
					}}
				/>

				{/* Client */}
				<div
					style={{
						...lineStyle,
						whiteSpace: "nowrap",
					}}
				>
					<span style={{ fontWeight: "bold" }}>Kunde: </span>
					{getClientDisplayName(client)}
				</div>

				{/* Patient */}
				{patientText && <div style={{ height: "0.5mm" }} />}
				{patientText && (
					<div
						style={{
							...lineStyle,
							whiteSpace: "normal",
							wordBreak: "break-word",
						}}
					>
						<span style={{ fontWeight: "bold" }}>Patient: </span>
						{patientText}
					</div>
				)}

				{/* Tooth color — placed under Patient when enabled */}
				{showToothColor && (
					<div
						style={{
							...lineStyle,
							whiteSpace: "nowrap",
						}}
					>
						<span style={{ fontWeight: "bold" }}>Zahnfarbe: </span>
						{job.tooth_color}
					</div>
				)}
			</div>
		</div>
	);
};
